import { runtimeConfig } from '@/config/runtime-config';
import type { TelegramProductShortResponseDto } from '@/generated';
import { defaultAppLocale, isSupportedLocale, type AppLocale } from '@/i18n/locales';

type ProductGeo = Pick<
  TelegramProductShortResponseDto,
  'city' | 'region' | 'country' | 'countryId' | 'regionId' | 'cityId'
>;

export type GeoSelectionPlacementInput = {
  cityId?: string | null;
  regionId?: string | null;
  countryId?: string | null;
  cityName?: string | null;
  regionName?: string | null;
  countryName?: string | null;
  locale?: string;
};

const getWlCountryName = (locale?: string): string => {
  const names = runtimeConfig.defaultCountry.name as Record<string, string>;
  const resolvedLocale = locale && isSupportedLocale(locale) ? locale : defaultAppLocale;

  return names[resolvedLocale] ?? names[runtimeConfig.defaultLanguage as AppLocale] ?? Object.values(names)[0] ?? '';
};

export const getGeoSelectionPlacementLabel = ({
  cityId,
  regionId,
  countryId,
  cityName,
  regionName,
  countryName,
  locale,
}: GeoSelectionPlacementInput): string | null => {
  const hasCity = Boolean(cityId?.trim());
  const hasRegion = Boolean(regionId?.trim());
  const hasCountry = Boolean(countryId?.trim());

  if (hasCity) {
    return cityName?.trim() || null;
  }

  if (hasRegion) {
    return regionName?.trim() || null;
  }

  if (hasCountry) {
    const name = countryName?.trim();
    if (name) {
      return name;
    }

    if (!runtimeConfig.isMultiCountry) {
      return getWlCountryName(locale) || null;
    }

    return null;
  }

  return null;
};

export const getProductPlacementLabel = (product: ProductGeo, locale?: string): string | null =>
  getGeoSelectionPlacementLabel({
    cityId: product.cityId ?? product.city?.id,
    regionId: product.regionId ?? product.region?.id,
    countryId: product.countryId ?? product.country?.id,
    cityName: product.city?.name,
    regionName: product.region?.name,
    countryName: product.country?.name,
    locale,
  });
